/**
 * 로그 화면 데이터(§7.5 재연결 규칙).
 * 화면을 열면 마지막 500건을 읽고, 그 뒤는 `xplanner://log-batch` 이벤트로 이어 붙인다.
 * 읽는 동안 들어온 이벤트는 모아 두었다가 페이지 뒤에 id로 중복 없이 붙인다(ADV-G06).
 * 일시정지는 화면 갱신만 멈춘다. 수집·디스크 기록은 계속된다.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { invoke, isDesktop, listen } from "@/desktop/runtime";
import { appendUnique, matchesFilter, prependUnique, toQuery, type LogBatch, type LogEntry, type LogFilter, type LogPage } from "./log-model";

export interface ActivityLogState {
  items: LogEntry[];
  total: number;
  matched: number;
  corruptLines: number;
  enabled: boolean;
  writeError: string | null;
  nextBefore: string | null;
  loading: boolean;
  loadingOlder: boolean;
  error: string | null;
  /** 폭주 때 화면 전달에서 뺀 건수(디스크에는 모두 있다) */
  dropped: number;
  /** 일시정지 중 쌓인 건수 */
  held: number;
}

const INITIAL: ActivityLogState = {
  items: [],
  total: 0,
  matched: 0,
  corruptLines: 0,
  enabled: true,
  writeError: null,
  nextBefore: null,
  loading: true,
  loadingOlder: false,
  error: null,
  dropped: 0,
  held: 0,
};

export const PAGE_SIZE = 500;

export function useActivityLog(filter: LogFilter, paused: boolean, onErrors?: (count: number) => void) {
  const [state, setState] = useState<ActivityLogState>(INITIAL);
  const filterRef = useRef(filter);
  filterRef.current = filter;
  const pausedRef = useRef(paused);
  pausedRef.current = paused;
  const onErrorsRef = useRef(onErrors);
  onErrorsRef.current = onErrors;
  const loadingRef = useRef(false);
  const pendingRef = useRef<{ items: LogEntry[]; total: number }>({ items: [], total: 0 });
  const heldRef = useRef<{ items: LogEntry[]; total: number; dropped: number }>({ items: [], total: 0, dropped: 0 });
  const filterKey = JSON.stringify(filter);

  const load = useCallback(async () => {
    if (!isDesktop()) {
      setState({ ...INITIAL, loading: false });
      return;
    }
    loadingRef.current = true;
    pendingRef.current = { items: [], total: 0 };
    heldRef.current = { items: [], total: 0, dropped: 0 };
    setState((s) => ({ ...s, loading: true, error: null, held: 0 }));
    try {
      const page = await invoke<LogPage>("read_activity_log_page", { query: toQuery(filterRef.current, Date.now(), { limit: PAGE_SIZE }) });
      const pending = pendingRef.current;
      const pageIds = new Set(page.items.map((e) => e.id));
      const extra = pending.items.filter((e) => !pageIds.has(e.id));
      setState((s) => ({
        ...s,
        items: appendUnique(page.items, extra),
        total: page.total + Math.max(0, pending.total - (pending.items.length - extra.length)),
        matched: page.matched + extra.length,
        corruptLines: page.corruptLines,
        enabled: page.enabled,
        writeError: page.writeError,
        nextBefore: page.nextBefore,
        loading: false,
      }));
    } catch (e) {
      setState((s) => ({ ...s, loading: false, error: e instanceof Error ? e.message : "로그를 읽지 못했습니다" }));
    } finally {
      loadingRef.current = false;
    }
    // filterKey가 바뀌면 다시 읽는다
  }, [filterKey]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    if (!isDesktop()) return;
    let alive = true;
    let off: (() => void) | null = null;
    void listen<LogBatch>("xplanner://log-batch", (batch) => {
      const now = Date.now();
      const hits = batch.items.filter((e) => matchesFilter(e, filterRef.current, now));
      const errors = batch.items.filter((e) => e.level === "error").length;
      if (errors) onErrorsRef.current?.(errors);
      if (loadingRef.current) {
        pendingRef.current.items.push(...hits);
        pendingRef.current.total += batch.items.length;
        return;
      }
      if (pausedRef.current) {
        const h = heldRef.current;
        h.items.push(...hits);
        h.total += batch.items.length;
        h.dropped += batch.dropped;
        setState((s) => ({ ...s, held: h.items.length }));
        return;
      }
      setState((s) => ({
        ...s,
        items: appendUnique(s.items, hits),
        total: s.total + batch.items.length,
        matched: s.matched + hits.length,
        dropped: s.dropped + batch.dropped,
      }));
    }).then((fn) => (alive ? (off = fn) : fn()));
    return () => {
      alive = false;
      off?.();
    };
  }, []);

  // 일시정지를 풀면 쌓아 둔 항목을 한 번에 붙인다.
  useEffect(() => {
    if (paused) return;
    const h = heldRef.current;
    if (!h.items.length && !h.total && !h.dropped) return;
    heldRef.current = { items: [], total: 0, dropped: 0 };
    setState((s) => ({
      ...s,
      items: appendUnique(s.items, h.items),
      total: s.total + h.total,
      matched: s.matched + h.items.length,
      dropped: s.dropped + h.dropped,
      held: 0,
    }));
  }, [paused]);

  const loadOlder = useCallback(async () => {
    const before = state.nextBefore;
    if (!before || state.loadingOlder) return;
    setState((s) => ({ ...s, loadingOlder: true }));
    try {
      const page = await invoke<LogPage>("read_activity_log_page", { query: toQuery(filterRef.current, Date.now(), { before, limit: PAGE_SIZE }) });
      setState((s) => ({ ...s, items: prependUnique(s.items, page.items), nextBefore: page.nextBefore, loadingOlder: false }));
    } catch {
      setState((s) => ({ ...s, loadingOlder: false }));
    }
  }, [state.nextBefore, state.loadingOlder]);

  return { ...state, reload: load, loadOlder };
}
